import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * CustomAccordion - A reusable accordion for displaying grouped info panels
 * Props:
 * - items: Array of { icon, title, content, coords? }
 * - defaultExpanded: index of initially expanded item (optional)
 * - onChange: callback when expanded item changes (optional)
 * - sx: style overrides
 */
const CustomAccordion = ({ items = [], defaultExpanded = null, onChange, sx = {} }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleChange = idx => {
    setExpanded(expanded === idx ? null : idx);
    if (onChange) onChange(idx, items[idx]);
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {items.map((item, idx) => (
        <Box key={idx} sx={{ width: '100%', position: 'relative', minHeight: 80, mb: idx < items.length - 1 ? 2 : 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 0,
              py: 2,
              cursor: 'pointer',
              userSelect: 'none',
              gap: 2,
              backgroundColor: 'transparent',
              position: 'relative',
            }}
            onClick={() => handleChange(idx)}
          >
            {item.icon && <Box sx={{ mr: 2 }}>{item.icon}</Box>}
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>{item.title}</Typography>
            <IconButton size="small" sx={{ transform: expanded === idx ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
          {/* Single animated border using framer-motion */}
          <motion.div
            initial={false}
            animate={{
              backgroundColor: expanded === idx ? '#6511af' : '#e0e0e0',
              top: expanded === idx ? 'calc(100% + 8px)' : '70px',
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              width: '100%',
              height: '2px',
              borderRadius: 1,
              position: 'absolute',
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          />
          <AnimatePresence initial={false}>
            {expanded === idx && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <Box sx={{ px: 0, py: 2, position: 'relative' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>{item.content}</Typography>
                  {item.coords && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'primary.main' }}>
                      Coordinates: {item.coords.lat}, {item.coords.lng}
                    </Typography>
                  )}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      ))}
    </Box>
  );
};

export default CustomAccordion;
